import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PageShell from "../components/PageShell";
import { useAuth } from "../context/AuthContext";
import "./EmployeeDirectory.css";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "processed", label: "Processed" },
  { value: "failed", label: "Failed" },
];

export default function AdminTranscriptIngest() {
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "company_admin";
  const listUrl = isCompanyAdmin
    ? "/api/admin/transcript-ingests"
    : "/api/transcript-ingests";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(50);
  const [replayBusy, setReplayBusy] = useState(null);

  const load = useCallback(async () => {
    setError("");
    setNotice("");
    setLoading(true);
    try {
      const params = { limit: Math.min(200, Math.max(1, Number(limit) || 50)) };
      if (status) params.status = status;
      const res = await axios.get(listUrl, {
        withCredentials: true,
        params,
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      setError("Could not load transcript ingests.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status, limit, listUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const onReplay = async (ingestId) => {
    setNotice("");
    setError("");
    setReplayBusy(ingestId);
    try {
      const res = await axios.post(
        `/api/admin/transcript-ingests/${ingestId}/replay`,
        {},
        { withCredentials: true },
      );
      if (res.status === 202) {
        setNotice("Replay queued. Refresh in a few seconds to see updated status.");
        await load();
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        (e?.response?.status === 409
          ? "That ingest cannot be replayed (not failed)."
          : "Replay failed.");
      setError(msg);
    } finally {
      setReplayBusy(null);
    }
  };

  const formatKeys = (keys) => {
    if (!Array.isArray(keys) || keys.length === 0) return "—";
    const s = keys.join(", ");
    return s.length > 48 ? `${s.slice(0, 45)}…` : s;
  };

  return (
    <PageShell
      title="Meeting transcript ingests"
      subtitle={
        isCompanyAdmin
          ? "Webhook payloads from your meeting platform (e.g. Comm360). Replay only applies to failed rows."
          : "Webhook payloads from your meeting platform (e.g. Comm360)."
      }
    >
      <div className="edAlerts">
        {error ? <div className="pill edAlert edAlert--error">{error}</div> : null}
        {notice ? <div className="pill edAlert edAlert--success">{notice}</div> : null}
      </div>

      <div className="edFormGrid" style={{ marginBottom: 16, maxWidth: 720 }}>
        <label style={{ fontSize: 13 }}>
          Status
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ marginTop: 6, width: "100%" }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || "all"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>
          Limit
          <input
            type="number"
            min={1}
            max={200}
            className="input"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            style={{ marginTop: 6, width: "100%" }}
          />
        </label>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <button type="button" className="btn btn--secondary" onClick={() => load()}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <p className="edReadOnly">Loading…</p>
      ) : items.length === 0 ? (
        <div className="edEmpty">No ingests match this filter.</div>
      ) : (
        <div className="edTableWrap">
          <table className="table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th>Created</th>
                <th>Status</th>
                <th>Source</th>
                <th>Idempotency</th>
                <th>Transcript</th>
                <th>Payload keys</th>
                {isCompanyAdmin ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td className="edReadOnly" style={{ whiteSpace: "nowrap" }}>
                    {row.created_at
                      ? new Date(row.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <span
                      style={{
                        fontWeight: 600,
                        color:
                          row.processing_status === "failed"
                            ? "var(--danger, #b91c1c)"
                            : undefined,
                      }}
                    >
                      {row.processing_status}
                    </span>
                  </td>
                  <td>{row.source || "—"}</td>
                  <td className="edReadOnly" title={row.idempotency_key || ""}>
                    {row.idempotency_key
                      ? row.idempotency_key.length > 20
                        ? `${row.idempotency_key.slice(0, 18)}…`
                        : row.idempotency_key
                      : "—"}
                  </td>
                  <td className="edReadOnly" title={row.transcript_preview || ""}>
                    {row.transcript_preview || "—"}
                  </td>
                  <td className="edReadOnly" title={(row.payload_top_level_keys || []).join(", ")}>
                    {formatKeys(row.payload_top_level_keys)}
                  </td>
                  {isCompanyAdmin ? (
                    <td>
                      {row.processing_status === "failed" ? (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          disabled={replayBusy === row.id}
                          onClick={() => onReplay(row.id)}
                        >
                          {replayBusy === row.id ? "Queuing…" : "Replay"}
                        </button>
                      ) : (
                        <span className="edReadOnly">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
